import { memo, useMemo, useState } from "react"
import { Table, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow, TableSortLabel } from "@mui/material"
import { ColumnDefinition } from "."
import { RootState } from "../../../state/configureStore"
import { useSelector } from "react-redux"
import { useSearchParams, useNavigate } from "react-router-dom"

export const TableView = <T extends object,>(
    {
        items = [],
        columns,
        columns_mobile = columns,
        defaultSortColumn,
        onRowClick = () => { },
        isClickable,
        paging,
        rowsPerPage
    }: {
        columns: Array<ColumnDefinition<T>>
        columns_mobile?: Array<ColumnDefinition<T>>
        items: Array<T>
        defaultSortColumn: keyof T & string
        onRowClick?: (item: T) => void
        isClickable?: true,
        paging: boolean,
        rowsPerPage: number
    }
) => {

    const [queryStrings, setQueryStrings] = useSearchParams()
    const pageFromUrl = Number(queryStrings.get("page")) || 0
    const perPageFromUrl = Number(queryStrings.get("perPage")) || rowsPerPage || 10

    const [page, setPage] = useState(pageFromUrl)
    const [itemsPerPage, setItemsPerPage] = useState(perPageFromUrl)

    const isMobile = useSelector<RootState>(state => state.dimensions.device) === "mobile"
    const descending = queryStrings.has('descending')
    const sortColumn = queryStrings.get('sortCol') ?? defaultSortColumn
    const navigate = useNavigate()

    const updateQuery = (page: number, perPage: number) => {
        const newParams = new URLSearchParams(queryStrings)
        newParams.set("page", String(page))
        newParams.set("perPage", String(perPage))
        setQueryStrings(newParams, { replace: true })
    }

    const handleSort = (property: string) => {
        if (property === sortColumn) {
            if (!descending) {
                queryStrings.set("descending", "true");
            } else {
                queryStrings.delete("descending");
            }
        } else {
            queryStrings.set("sortCol", property);
            if (descending) {
                queryStrings.set("descending", "true");
            } else {
                queryStrings.delete("descending");
            }
        }
        navigate('?' + queryStrings.toString())
    }

    const sorted = useMemo(() => {
        const selectedColumn = columns.find(c => c.Property === sortColumn)
        if (!selectedColumn || selectedColumn.Sortable === false) {
            return items;
        }
        return [...items]
            .sort((a, b) => {
                if (selectedColumn?.sortFn) {
                    return descending ? selectedColumn.sortFn(b, a) : selectedColumn.sortFn(a, b)
                }
                const aVal = a[sortColumn]?.toLowerCase()
                const bVal = b[sortColumn]?.toLowerCase()

                if (aVal < bVal)
                    return descending ? 1 : -1

                if (aVal > bVal)
                    return descending ? -1 : 1

                return 0
            })
    }, [items, sortColumn, descending, columns])

    const paged = useMemo(() => {
        const start = page * itemsPerPage
        return sorted.slice(start, start + itemsPerPage)
    }, [sorted, page, itemsPerPage])

    return (
        <Table>
            <TableHead>
                <TableRow>
                    {(isMobile ? columns_mobile : columns).map((c, idx) => (
                        <TableHeader
                            key={idx}
                            title={c.Title ?? c.Property}
                            property={c.Property}
                            sorted={c.Property === sortColumn} ascending={!descending}
                            onSort={c.Sortable === false ? undefined : handleSort} />))}
                </TableRow>
            </TableHead>
            <TableBody>
                {paged.map((i, idx) => (
                    <TableRow key={idx}
                        sx={{
                            cursor: isClickable ? 'pointer' : 'default'
                        }}
                        hover={isClickable}
                    >
                        {(isMobile ? columns_mobile : columns)
                            .map((c, idx) => (<TableColumnContent<T> key={idx} column={c} item={i} onCellClick={(item) => onRowClick(item)} />))}
                    </TableRow>
                ))}
            </TableBody>
            {paging && (
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            count={sorted.length}
                            page={page}
                            onPageChange={(_, newPage) => {
                                setPage(newPage)
                                updateQuery(newPage, itemsPerPage)
                            }}
                            rowsPerPage={itemsPerPage}
                            onRowsPerPageChange={(e) => {
                                const newPerPage = Number(e.target.value)
                                setItemsPerPage(newPerPage)
                                setPage(0)
                                updateQuery(0, newPerPage)
                            }}
                            rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        />
                    </TableRow>
                </TableFooter>
            )}
        </Table>
    )
}


const TableColumnContent = memo(<T extends object,>({
    column,
    item,
    onCellClick = () => { }
}: {
    column: ColumnDefinition<T>
    item: T
    onCellClick?: (item: T, column: ColumnDefinition<T>) => void
}) => {
    const value = column.Render ? column.Render(item) : item[column.Property] as string

    return (
        <TableCell onClick={() => onCellClick(item, column)}>
            {value}
        </TableCell>
    )
})

const TableHeader = ({ title, property, sorted, ascending, onSort }: { title: string, property: string, sorted: boolean, ascending: boolean, onSort?: (property: string) => void }) => {

    const handleClick = (e) => {
        if (onSort) onSort(property)
    }

    return (
        <TableCell sx={{ fontFamily: 'DIN' }}>
            <TableSortLabel
                hideSortIcon={onSort ? false : true}
                active={sorted}
                direction={ascending ? "asc" : "desc"}
                sx={{ ":hover": { cursor: onSort ? "pointer" : "default" } }}
                onClick={handleClick}
            >
                {title}
            </TableSortLabel>
        </TableCell>
    )
}